import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { doc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';

export default function MemberRoleSelect(props) {
  const {
    defaultValue, organizationId, userId, userExists,
  } = props;
  const [value, setValue] = useState(defaultValue);
  const [isUpdating, setIsUpdating] = useState(false);
  const db = useFirestore();
  const { t } = useTranslation();

  const handleRoleChange = async (newRole) => {
    if (newRole === value || isUpdating) return;

    setIsUpdating(true);
    setValue(newRole); // Optimistic update

    try {
      if (userExists) {
        const docRef = doc(db, 'organizations', organizationId, 'members', userId);
        await setDoc(docRef, { role: newRole }, { merge: true });
      } else {
        const docRef = doc(db, 'pendingOrganizationInvitations', userId);
        await setDoc(docRef, { role: newRole }, { merge: true });
      }
    } catch (error) {
      // Revert on error
      setValue(defaultValue);
      // Silently handle error - could add toast notification here
    } finally {
      setIsUpdating(false);
    }
  };

  const getRoleConfig = (role) => {
    const configs = {
      admin: {
        label: t('member_role.admin'),
        color: 'error',
        icon: <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />,
      },
      member: {
        label: t('member_role.member'),
        color: 'primary',
        icon: <PersonIcon sx={{ fontSize: 16 }} />,
      },
    };
    return configs[role] || configs.member;
  };

  const roleConfig = getRoleConfig(value);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Role Display */}
      <Chip
        icon={roleConfig.icon}
        label={roleConfig.label}
        color={roleConfig.color}
        size="small"
        variant="filled"
        sx={{
          minWidth: 80,
          '& .MuiChip-label': {
            fontWeight: 'medium',
          },
        }}
      />

      {/* Role Selector */}
      <Select
        value={value}
        onChange={(e) => handleRoleChange(e.target.value)}
        size="small"
        disabled={isUpdating}
        sx={{
          minWidth: 100,
          '& .MuiSelect-select': {
            py: 0.5,
            fontSize: '0.875rem',
          },
        }}
      >
        <MenuItem value="member">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: 16 }} />
            {t('member_role.member')}
          </Box>
        </MenuItem>
        <MenuItem value="admin">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 16 }} />
            {t('member_role.admin')}
          </Box>
        </MenuItem>
      </Select>

      {/* Loading indicator */}
      {isUpdating && (
        <CircularProgress size={16} />
      )}
    </Box>
  );
}
